<template>
    <div class="ai-sales-content">
        <dl v-if="hasPurchaseRecap" class="ai-sales-content__purchase-recap" :class="recapClass">
            <div
                v-for="yearlyRecap in formattedPurchaseRecap"
                :key="yearlyRecap.year"
                class="ai-sales-content__yearly-recap"
            >
                <dt class="ai-sales-content__year typographies-paragraph-medium-regular">
                    <CompassIcon icon="calendar" size="small" /> {{ yearlyRecap.year }}
                </dt>
                <dd class="ai-sales-content__yearly-amount typographies-paragraph-medium-semibold">
                    {{ yearlyRecap.amount | numberformatCurrency }}
                </dd>
            </div>
        </dl>
        <AIContentList
            v-if="hasMargin"
            :hasMargin="hasBestSellers || hasAveragePaymentTime || hasLatePayments"
        >
            <h4 class="typographies-paragraph-medium-semibold">
                {{ $t.__('overview.tabs.TabSummary.salesContent.purchasing_trends') }}
            </h4>
            <ul>
                <li v-if="purchaseBehavior.average_purchase_frequency_per_year > 0">
                    {{ $t.__('overview.tabs.TabSummary.salesContent.frequency') }} :
                    {{ formattedAverage }}
                    {{
                        $t.n__(
                            'overview.tabs.TabSummary.salesContent.annual_purchases',
                            null,
                            purchaseBehavior.average_purchase_frequency_per_year,
                            purchaseBehavior.average_purchase_frequency_per_year,
                        )
                    }}
                </li>
                <li v-if="purchaseBehavior.average_amount_per_purchase > 0">
                    {{ $t.__('overview.tabs.TabSummary.salesContent.average_cart') }} :
                    {{ purchaseBehavior.average_amount_per_purchase | numberformatCurrency }}
                </li>
            </ul>
        </AIContentList>
        <template v-if="hasBestSellers">
            <AIContentList :hasMargin="hasAveragePaymentTime || hasLatePayments">
                <h4 class="typographies-paragraph-medium-semibold">
                    {{ $t.__('overview.tabs.TabSummary.salesContent.best_selling_products') }}
                </h4>
                <ul>
                    <li v-for="item in bestSellers.by_quantity" :key="item.id">
                        {{ item.name }} : {{ getFormattedQuantity(item.total_quantity) }}
                        {{
                            $t.n__(
                                'common.labels.units',
                                null,
                                getFormattedQuantity(item.total_quantity),
                                getFormattedQuantity(item.total_quantity),
                            )
                        }}
                    </li>
                </ul>
            </AIContentList>
            <AIContentList :hasMargin="hasAveragePaymentTime || hasLatePayments">
                <h4 class="typographies-paragraph-medium-semibold">
                    {{ $t.__('overview.tabs.TabSummary.salesContent.high_turnover_products') }}
                </h4>
                <ul>
                    <li v-for="item in bestSellers.by_amount" :key="item.id">
                        {{ item.name }} : {{ item.total_amount | numberformatCurrency }}
                    </li>
                </ul>
            </AIContentList>
        </template>

        <p v-if="hasAveragePaymentTime" class="ai-document-content__payment-average">
            <span class="typographies-paragraph-medium-semibold">
                {{ $t.__('overview.tabs.TabSummary.paymentContent.average_payment') }} :
            </span>
            {{ financialRelationship.average_payment_time_days | numberformatWithOptions({precision: 0}, false) }} {{ $t.__('common.labels.days') }}
        </p>
        <p v-if="hasLatePayments" class="ai-document-content__late-payments">
            <span class="typographies-paragraph-medium-semibold">
                {{ $t.__('overview.tabs.TabSummary.paymentContent.late_payments') }} :
            </span>
            {{ financialRelationship.late_payments_count }}
        </p>
        <NoInvoicingData v-if="hasNoData" />
    </div>
</template>
<script>
import NoInvoicingData from 'vcomponents/AISummary/Content/NoInvoicingData.vue';
import AIContentList from 'vcomponents/AISummary/Content/AIContentList.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapState: corpMapState } = createNamespacedHelpers('corp');

export default {
    components: {
        NoInvoicingData,
        AIContentList,
    },
    inject: ['getSummary'],
    computed: {
        ...corpMapState(['numformat']),
        summary() {
            return this.getSummary();
        },
        purchaseBehavior() {
            return this.summary
                ? this.summary.summary.input.customer_summary.purchase_behaviour
                : {};
        },
        financialRelationship() {
            return this.summary &&
                this.summary.summary.input.customer_summary.financial_relationship
                ? this.summary.summary.input.customer_summary.financial_relationship
                : {};
        },
        hasMargin() {
            return (
                this.purchaseBehavior.average_purchase_frequency_per_year > 0 ||
                this.purchaseBehavior.average_amount_per_purchase > 0 ||
                this.hasAveragePaymentTime ||
                this.hasLatePayments
            );
        },
        hasPurchaseRecap() {
            if (!this.purchaseBehavior.annual_purchase_amounts) {
                return false;
            }
            return Object.keys(this.purchaseBehavior.annual_purchase_amounts).length;
        },
        formattedPurchaseRecap() {
            const recap = [...this.purchaseBehavior.annual_purchase_amounts];
            if (recap.length < 3) {
                return recap;
            } else {
                return recap.slice(Math.max(recap.length - 3, 0));
            }
        },
        formattedAverage() {
            if (this.purchaseBehavior.average_purchase_frequency_per_year % 1 === 0) {
                return this.purchaseBehavior.average_purchase_frequency_per_year;
            }

            return NumberFormat.formatToDisplay(
                this.purchaseBehavior.average_purchase_frequency_per_year,
                this.numformat,
            );
        },
        recapClass() {
            return {
                'ai-sales-content__purchase-recap--has-margin': this.hasMargin,
            };
        },
        hasNoData() {
            return (
                !this.hasPurchaseRecap &&
                !this.purchaseBehavior.average_purchase_frequency_per_year > 0 &&
                !this.purchaseBehavior.average_amount_per_purchase > 0 &&
                !this.hasBestSellers &&
                !this.hasAveragePaymentTime &&
                !this.hasLatePayments
            );
        },
        hasAveragePaymentTime() {
            return (
                Object.keys(this.financialRelationship).includes('average_payment_time_days') &&
                this.financialRelationship.average_payment_time_days >= 1
            );
        },
        hasLatePayments() {
            return Object.keys(this.financialRelationship).includes('late_payments_count');
        },
        bestSellers() {
            return this.summary?.summary.input.customer_summary.best_sold_items
                ? this.summary.summary.input.customer_summary.best_sold_items
                : {};
        },
        hasBestSellers() {
            return Object.keys(this.bestSellers).length > 0;
        },
    },
    methods: {
        getFormattedQuantity(quantity) {
            return parseInt(quantity, 10);
        },
    },
};
</script>
<style lang="scss" scoped>
.ai-sales-content {
    h4 {
        margin: 0;
    }

    ul {
        list-style-type: none;
        margin: 0;
        padding: 0;
    }

    p {
        margin: 0 0 var(--spacing-3);

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__purchase-recap {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-start;
        margin: 0;
        padding: 0 0 var(--spacing-3);

        &--has-margin {
            margin-bottom: var(--spacing-3);
        }
    }

    &__yearly-recap {
        display: flex;
        flex-direction: column-reverse;
        gap: var(--spacing-0);
    }

    &__year {
        color: var(--colors-common-text-standard-caption);
        display: flex;
        gap: var(--spacing-0);
    }

    &__yearly-amount {
        margin: 0;
    }
}
</style>
